import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import S, { C } from './styles';

export function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{ background:C.surface, border:`1px solid ${C.goldBorder}`, borderRadius:14, padding:28, width:500, maxWidth:'95vw', maxHeight:'90vh', overflow:'auto', boxShadow:'0 8px 40px rgba(0,0,0,.15)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:C.text, fontFamily:"'Inter',sans-serif" }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.text3, fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DigitalClientsPage() {
  const { canEdit, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({ name:'', industry:'', contact:'', notes:'' });
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  const load = () => api.getClients().then(setClients).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  function openCreate() { setForm({ name:'', industry:'', contact:'', notes:'' }); setModal('new'); }
  function openEdit(c)  { setForm({ name:c.name, industry:c.industry||'', contact:c.contact||'', notes:c.notes||'' }); setModal(c); }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'new') await api.createClient(form);
      else await api.updateClient(modal.id, form);
      await load();
      setModal(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Archive this client?')) return;
    await api.deleteClient(id);
    load();
  }

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry||'').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
        <div>
          <h1 style={S.h1}>Clients</h1>
          <div style={{ fontSize:13, color:C.text3, marginTop:3 }}>Digital planning clients</div>
        </div>
        {canEdit && <button style={S.btnPrimary} onClick={openCreate}>+ New Client</button>}
      </div>

      <input placeholder="Search clients…" value={search} onChange={e=>setSearch(e.target.value)} style={S.search} />

      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={S.empty}>{search ? 'No clients match your search.' : 'No clients yet. Create your first client.'}</div>
      ) : (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Client','Industry','Contact','Plans',''].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const total = (c.plan_count || 0);
                return (
                  <tr key={c.id} style={S.tr}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
                    onMouseLeave={e=>e.currentTarget.style.background=C.surface}>
                    <td style={S.td}>
                      <div style={{ fontWeight:700, color:C.text }}>{c.name}</div>
                    </td>
                    <td style={S.td}><span style={S.muted}>{c.industry||'—'}</span></td>
                    <td style={S.td}><span style={S.muted}>{c.contact||'—'}</span></td>
                    <td style={S.td}>
                      <span style={{ color:C.gold, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>{total}</span>
                      {c.plans_in_review > 0 && (
                        <span style={{ marginLeft:8, fontSize:11, color:C.amber }}>{c.plans_in_review} in review</span>
                      )}
                    </td>
                    <td style={{ ...S.td, textAlign:'right' }} onClick={e=>e.stopPropagation()}>
                      {canEdit && <button style={S.btnGhost} onClick={()=>openEdit(c)}>Edit</button>}
                      {isAdmin && (
                        <button style={{ ...S.btnAction, color:C.red, marginLeft:6 }} onClick={()=>handleDelete(c.id)}>Archive</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal==='new' ? 'New Client' : `Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          {[
            { key:'name',     label:'Client Name *', placeholder:'e.g. Coca-Cola Moldova' },
            { key:'industry', label:'Industry',       placeholder:'e.g. FMCG, Telecom' },
            { key:'contact',  label:'Contact',        placeholder:'Contact person or email' },
          ].map(f=>(
            <label key={f.key} style={{ display:'block', marginBottom:14 }}>
              <span style={S.label}>{f.label}</span>
              <input value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} style={S.input} />
            </label>
          ))}
          <label style={{ display:'block', marginBottom:22 }}>
            <span style={S.label}>Notes</span>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} style={{ ...S.input, resize:'vertical' }} />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={S.btnGhost} onClick={()=>setModal(null)}>Cancel</button>
            <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : modal==='new' ? 'Create Client' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
